'use client';

/**
 * Project Card - Hero Context Card
 *
 * Shows the currently selected project as context for simulations/analysis.
 * Displayed at the top of Obeya explorer when a project is selected.
 *
 * @layer explorer
 * @arch_domain obeya
 */

import {
  LayoutDashboard,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  FileText,
  Package,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { RAGStatus } from '@/lib/api/types';

const RAGIcon = {
  red: AlertCircle,
  amber: AlertTriangle,
  green: CheckCircle,
  unknown: LayoutDashboard,
};

const RAGColors = {
  red: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/30',
    text: 'text-red-500',
    badge: 'bg-red-500/20 text-red-400',
  },
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-500',
    badge: 'bg-amber-500/20 text-amber-400',
  },
  green: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    text: 'text-green-500',
    badge: 'bg-green-500/20 text-green-400',
  },
  unknown: {
    bg: 'bg-muted/50',
    border: 'border-border',
    text: 'text-muted-foreground',
    badge: 'bg-muted text-muted-foreground',
  },
};

export interface ProjectCardData {
  semantic_id: string;
  name: string;
  rag_status: RAGStatus;
  rag_label: string;
  rfq_count: number;
  po_count: number;
}

interface ProjectCardProps {
  project: ProjectCardData;
  onClear?: () => void;
  compact?: boolean;
}

export function ProjectCard({ project, onClear, compact = false }: ProjectCardProps) {
  // Debug: log the full project object
  console.log('[ProjectCard] Received project:', JSON.stringify(project, null, 2));

  const status = project.rag_status;
  const colors = RAGColors[status] || RAGColors.unknown;
  const Icon = RAGIcon[status] || RAGIcon.unknown;

  if (compact) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-md border',
          colors.bg,
          colors.border
        )}
      >
        <Icon className={cn('w-4 h-4 shrink-0', colors.text)} />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium truncate block">
            {project.name || project.semantic_id}
          </span>
          {project.name && project.semantic_id && (
            <span className="text-xs text-muted-foreground font-mono truncate block">
              {project.semantic_id}
            </span>
          )}
        </div>
        {onClear && (
          <button
            onClick={onClear}
            className="p-0.5 rounded hover:bg-background/50 transition-colors"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        colors.bg,
        colors.border
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={cn('w-5 h-5 shrink-0', colors.text)} />
          <div className="min-w-0">
            <h3 className="font-medium text-sm truncate">{project.name}</h3>
            <span className={cn('text-xs px-1.5 py-0.5 rounded', colors.badge)}>
              {project.rag_label}
            </span>
          </div>
        </div>
        {onClear && (
          <button
            onClick={onClear}
            className="p-1 rounded hover:bg-background/50 transition-colors shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <FileText className="w-3.5 h-3.5" />
          <span>{project.rfq_count} RFQs</span>
        </div>
        <div className="flex items-center gap-1">
          <Package className="w-3.5 h-3.5" />
          <span>{project.po_count} POs</span>
        </div>
      </div>
    </div>
  );
}
